import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { Trip } from '@/types';
import * as tripService from '@/services/tripService';
import TripDashboard from '@/components/trip/TripDashboard';
import Header from '@/components/layout/Header';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useI18n } from '@/hooks/useI18n';

const TripPage = () => {
  const { tripId, tab } = useParams<{ tripId: string; tab?: string }>();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();

  useEffect(() => {
    if (!tripId) {
      navigate('/');
      return;
    }

    const unsubscribe = tripService.listenToTripById(tripId, (updatedTrip) => {
      if (updatedTrip) {
        setTrip(updatedTrip);
      } else {
        console.log('Trip not found, navigating to home');
        navigate('/');
      }
      // Set loading to false only after the first data load
      if (loading) {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [tripId, navigate, loading]); // Fixed: Added loading dependency

  // This effect now correctly handles the loading state only when the tripId changes.
  useEffect(() => {
    setLoading(true);
  }, [tripId]);

  if (loading) {
    return <LoadingSpinner text={t('pages.tripPage.loadingTrip')} fullPage />;
  }

  if (!trip) {
    return null; 
  }

  const tripName = trip.name || t('pages.tripPage.defaultTripName', { tripId: trip.id });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header 
        title={tripName} 
        onBack={() => navigate('/dashboard')}
      />
      {/* The main content area will now grow and shrink correctly, and we delegate scrolling to the dashboard */}
      <Box component="main" sx={{ flex: 1, minHeight: 0 }}>
        <TripDashboard initialTab={tab} trip={trip} />
      </Box>
    </Box>
  );
};

export default TripPage;
